import type { RawAnomalyDto } from '../models/anomaly.types'
import { adaptAnomaly } from '../service/adaptAnomaly'

import type { AnomalyLoadApi, AnomalyLoadContext, AnomalyLoadStore } from './anomalyFlow.types'

import { ApiError } from '@/shared/http'

export interface LoadAnomaliesFlowDeps {
  api: AnomalyLoadApi
  store: AnomalyLoadStore
  context: AnomalyLoadContext
}

function uniqueWorkspaceIds(workspaceIds: readonly number[]): number[] {
  return [...new Set(workspaceIds.filter((id) => Number.isFinite(id)))]
}

function uniqueMetricFields(dtos: readonly RawAnomalyDto[]): string[] {
  return [...new Set(dtos.flatMap((dto) => dto.details.metrics).filter(Boolean))]
}

export class LoadAnomaliesFlow {
  constructor(private readonly deps: LoadAnomaliesFlowDeps) {}

  async execute(workspaceIds: readonly number[], signal?: AbortSignal): Promise<void> {
    const ids = uniqueWorkspaceIds(workspaceIds)
    if (ids.length === 0) return
    const key = ids.join(',')
    this.deps.store.setLoading(key)

    try {
      const dtos = await this.loadDtos(ids, signal)
      if (!this.isCurrent(key, signal)) return
      await this.deps.context.prepareMetrics(uniqueMetricFields(dtos))
      if (!this.isCurrent(key, signal)) return

      const now = this.deps.context.now()
      const records = dtos.flatMap((dto) => {
        const record = adaptAnomaly(dto, {
          formatMetric: this.deps.context.formatMetric,
          metricLabel: this.deps.context.metricLabel,
          isLowerBetter: this.deps.context.isLowerBetter,
          now,
          ...(this.deps.context.onMetricIndexFallback
            ? { onMetricIndexFallback: this.deps.context.onMetricIndexFallback }
            : {}),
        })
        return record ? [record] : []
      })
      this.deps.store.setRecords(key, records)
    } catch (cause) {
      if (!this.deps.store.isCurrent(key)) return
      const error = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
      if (error.kind !== 'canceled') this.deps.store.setError(key, error)
    }
  }

  private loadDtos(ids: readonly number[], signal?: AbortSignal): Promise<RawAnomalyDto[]> {
    const first = ids[0]
    return ids.length === 1 && first !== undefined
      ? this.deps.api.list(first, signal)
      : this.deps.api.listMany(ids, signal)
  }

  private isCurrent(key: string, signal?: AbortSignal): boolean {
    return signal?.aborted !== true && this.deps.store.isCurrent(key)
  }
}
